import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import useFetch from "../../../hooks/useFetch";

const CANDY_COLORS = [
  { fill: "#FF5A6E", shine: "#FFB3BC" },
  { fill: "#FFA94D", shine: "#FFD9AD" },
  { fill: "#FFD43B", shine: "#FFF0B0" },
  { fill: "#51CF66", shine: "#B7F0C1" },
  { fill: "#4DABF7", shine: "#B7E1FF" },
  { fill: "#FF6FB5", shine: "#FFC3E1" },
];

const DEFAULT_STREAK_GOAL = 7;
const DEFAULT_POINTS_GOAL = 10000;
const TOTAL_SLOTS = 28;

export default function Gumball({ history = [], streakInfo, onClose, gameKey = "tinythink" }) {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState?.user?._id;
  const userCode = authState?.user?.think5whyscode;
  const userName = authState?.user?.name;
  const token = authState?.token;

  const [popped, setPopped] = useState(false);
  const [config, setConfig] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const lifetimePoints = useMemo(
    () => (history || []).reduce((sum, day) => sum + (day.totalScore || 0), 0),
    [history]
  );
  const currentStreak = streakInfo?.currentStreak || 0;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetchData(
          { 
            url: `/gumball/${gameKey}/config`, 
            method: "post", 
            data: { userId, userCode, userName }, 
            headers: { Authorization: token } 
          },
          { silent: true }
        );
        if (mounted && res?.success) setConfig(res.config);
      } catch (e) {
        console.error("Gumball config fetch error:", e);
      }
    })();
    return () => { mounted = false; };
   }, [userId, gameKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const streakGoal = config?.streakGoal || DEFAULT_STREAK_GOAL;
  const pointsGoal = config?.pointsGoal || DEFAULT_POINTS_GOAL;
  const cycleStartPoints = config?.cycleStartPoints || 0;
  const cyclePoints = Math.max(lifetimePoints - cycleStartPoints, 0);

  const streakPct = Math.min(currentStreak / streakGoal, 1);
  const pointsPct = Math.min(cyclePoints / pointsGoal, 1);
  const fillPct = Math.max(streakPct, pointsPct);
  const hasWon = fillPct >= 1;
  const filledSlots = Math.round(fillPct * TOTAL_SLOTS);

  const winReason =
    streakPct >= 1 && pointsPct >= 1 ? "your streak AND your points" :
    streakPct >= 1 ? "your day streak" : "your points goal";

  const jiggle = useCallback(() => {
    if (hasWon) return;
    setPopped(true);
    setTimeout(() => setPopped(false), 600);
  }, [hasWon]);

  const gumballs = useMemo(
    () => Array.from({ length: TOTAL_SLOTS }, (_, i) => ({
      id: i,
      color: CANDY_COLORS[i % CANDY_COLORS.length],
      filled: i < filledSlots,
      delay: (i % 8) * 0.06,
    })),
    [filledSlots]
  );

  const handleEmptySubmit = async ({ rewardGiven, remarks, nextRewardDescription, nextRewardNote, nextStreakGoal, nextPointsGoal }) => {
    setSaving(true);
    try {
      const res = await fetchData(
        {
          url: `/gumball/${gameKey}/empty`,
          method: "post",
          data: {
            userId, userCode, userName,
            rewardGiven, remarks,
            lifetimePoints,
            nextRewardDescription, nextRewardNote, nextStreakGoal, nextPointsGoal,
          },
          headers: { Authorization: token },
        },
        { silent: false }
      );
      if (res?.success) {
        setConfig(res.config);
        setActiveModal(null);
      }
    } catch (e) {
      console.error("Empty gumball error:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleSetRewardSubmit = async ({ streakGoal: sg, pointsGoal: pg, rewardDescription, rewardNote }) => {
    setSaving(true);
    try {
      const res = await fetchData(
        {
          url: `/gumball/${gameKey}/set-reward`,
          method: "post",
          data: { userId, userCode, userName, streakGoal: sg, pointsGoal: pg, rewardDescription, rewardNote },
          headers: { Authorization: token },
        },
        { silent: false }
      );
      if (res?.success) {
        setConfig(res.config);
        setActiveModal(null);
      }
    } catch (e) {
      console.error("Set reward error:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-b from-[#3a0ca3] via-[#7C3AED] to-[#FF6FB5] flex flex-col overflow-hidden">
      <style>{`
        @keyframes gb-rise { 0% { transform: translateY(24px) scale(0.6); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
        @keyframes gb-jiggle { 0%, 100% { transform: translateY(0) rotate(0deg); } 25% { transform: translateY(-6px) rotate(-6deg); } 50% { transform: translateY(0) rotate(4deg); } 75% { transform: translateY(-3px) rotate(-3deg); } }
        @keyframes gb-burst { 0% { transform: translate(0,0) scale(1); opacity: 1; } 100% { transform: translate(var(--bx), var(--by)) scale(0.3); opacity: 0; } }
        @keyframes gb-confetti { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(540deg); opacity: 0.9; } }
        @keyframes gb-pop-in { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes gb-glow { 0%, 100% { box-shadow: 0 0 0px rgba(255,255,255,0.0); } 50% { box-shadow: 0 0 40px rgba(255,255,255,0.55); } }
        @keyframes gb-sheet-in { 0% { transform: translateY(100%); } 100% { transform: translateY(0); } }
        .gb-ball { animation: gb-rise 0.4s ease-out backwards; }
        .gb-ball.jiggle { animation: gb-jiggle 0.5s ease-in-out; }
        .gb-ball.burst { animation: gb-burst 0.9s cubic-bezier(.2,.9,.4,1) forwards; }
        .gb-dome { animation: gb-glow 2.4s ease-in-out infinite; }
        .gb-confetti-piece { animation: gb-confetti linear infinite; }
        .gb-win-badge { animation: gb-pop-in 0.5s cubic-bezier(.2,.9,.3,1.3) 0.2s backwards; }
        .gb-sheet { animation: gb-sheet-in 0.3s ease-out; }
      `}</style>

      {hasWon && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 30 }).map((_, i) => {
            const color = CANDY_COLORS[i % CANDY_COLORS.length].fill;
            const left = Math.random() * 100;
            const duration = 2.5 + Math.random() * 2;
            const delay = Math.random() * 2;
            const size = 6 + Math.random() * 6;
            return (
              <span key={i} className="gb-confetti-piece absolute top-0 rounded-sm"
                style={{ left: `${left}%`, width: size, height: size * 1.4, background: color, animationDuration: `${duration}s`, animationDelay: `${delay}s` }} />
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between px-4 pt-4">
        <span className="text-sm font-extrabold text-white/90 uppercase tracking-wider">🍬 Gumball Bonanza</span>
        <button onClick={onClose} aria-label="Close Gumball Bonanza"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white text-lg active:scale-90 transition-transform">
          <i className="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-start px-5 gap-5 overflow-y-auto pb-4">
        <button onClick={jiggle} aria-label="Tap the gumball machine" className="relative w-64 h-64 sm:w-72 sm:h-72 focus:outline-none flex-shrink-0">
          <div className="gb-dome absolute inset-0 rounded-full bg-white/10 border-4 border-white/40 backdrop-blur-sm overflow-hidden flex flex-wrap-reverse content-start items-end justify-center gap-1.5 p-6">
            {gumballs.map((g) => {
              if (!g.filled && !hasWon) {
                return <span key={g.id} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-white/20" aria-hidden="true" />;
              }
              const angle = Math.random() * 2 * Math.PI;
              const dist = 120 + Math.random() * 80;
              return (
                <span key={g.id} className={`gb-ball ${popped ? "jiggle" : ""} ${hasWon ? "burst" : ""} w-6 h-6 sm:w-7 sm:h-7 rounded-full relative`}
                  style={{ background: `radial-gradient(circle at 30% 30%, ${g.color.shine}, ${g.color.fill})`, animationDelay: `${g.delay}s`, "--bx": `${Math.cos(angle) * dist}px`, "--by": `${Math.sin(angle) * dist}px` }}
                  aria-hidden="true" />
              );
            })}
          </div>
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-20 h-10 bg-[#FF8B6B] rounded-b-2xl border-4 border-white/40" />
          <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 w-10 h-4 bg-[#7C3AED] rounded-full border-2 border-white/30" />
        </button>

        <div className="w-full max-w-xl space-y-3 mt-2">
          <ProgressRow emoji="🔥" label={`${currentStreak} / ${streakGoal} day streak`} pct={streakPct} barColor="#FF8B6B" />
          <ProgressRow emoji="⭐" label={`${cyclePoints.toLocaleString()} / ${pointsGoal.toLocaleString()} points`} pct={pointsPct} barColor="#4DABF7" />
        </div>

        {config?.rewardDescription && !hasWon && (
          <div className="w-full max-w-3xl bg-white/10 rounded-2xl px-4 py-2.5 text-center">
            <p className="text-xs text-white/90">
              🎁 Reward waiting: <span className="font-extrabold">{config.rewardDescription}</span>
            </p>
            {config.rewardNote && <p className="text-[11px] text-white/60 mt-0.5">{config.rewardNote}</p>}
          </div>
        )}

        {hasWon ? (
          <div className="gb-win-badge w-full max-w-full bg-white rounded-3xl shadow-xl p-5 text-center space-y-2 mt-2">
            <div className="text-5xl" aria-hidden="true">🏆</div>
            <h2 className="text-2xl font-extrabold text-[#7C3AED]">You Won!</h2>
            <p className="text-xs text-[#8B7FA8]">You hit {winReason} — that's a real prize-level achievement!</p>
            <p className="text-sm font-bold text-[#FF6FB5] pt-1">
              Ask your Mama, Papa, Grandparent, Teacher or Kins for your true gift! 🎁
            </p>
            <button onClick={onClose} className="w-full bg-[#7C3AED] text-white font-bold rounded-2xl py-3 mt-2 active:scale-95 transition-transform">
              Yay, Back to Fun!
            </button>
          </div>
        ) : (
          <p className="text-xs text-white text-center max-w-3xl">
            Tap the machine to jiggle the gumballs! Keep your streak going or collect more points to fill it up. 🎉
          </p>
        )}

        <div className="w-full max-w-2xl pt-3 mt-2 border-t border-white/15 space-y-2">
          <p className="text-xs lg:text-sm text-gray-800 text-center uppercase tracking-wider">For Parents / Teachers</p>
          <div className="flex gap-2">
            <button onClick={() => setActiveModal("setReward")} className="flex-1 w-fit px-2 text-xs font-semibold text-white/80 bg-white/10 hover:bg-white/15 rounded-xl py-2 transition-colors">
              ⚙️ Set Reward
            </button>
            <button onClick={() => setActiveModal("empty")} className="flex-1 w-fit px-2 text-xs font-semibold text-white/80 bg-white/10 hover:bg-white/15 rounded-xl py-2 transition-colors">
              🧺 Empty The Ball
            </button>
          </div>
        </div>
      </div>

      {activeModal === "empty" && (
        <EmptyBallModal onCancel={() => setActiveModal(null)} onSubmit={handleEmptySubmit} saving={saving} />
      )}
      {activeModal === "setReward" && (
        <SetRewardModal currentConfig={config} onCancel={() => setActiveModal(null)} onSubmit={handleSetRewardSubmit} saving={saving} />
      )}
    </div>
  );
}

function ProgressRow({ emoji, label, pct, barColor }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-xs" aria-hidden="true">{emoji}</span>
        <span className="text-xs font-bold text-white/90">{label}</span>
      </div>
      <div className="h-3 bg-white/20 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${pct * 100}%`, background: barColor }} />
      </div>
    </div>
  );
}

function EmptyBallModal({ onCancel, onSubmit, saving }) {
  const [rewardGiven, setRewardGiven] = useState("");
  const [remarks, setRemarks] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [nextRewardDescription, setNextRewardDescription] = useState("");
  const [nextRewardNote, setNextRewardNote] = useState("");

  const canSubmit = rewardGiven.trim().length > 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end" onClick={onCancel}>
      <div className="gb-sheet w-full bg-gray-800 text-white rounded-t-3xl p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm lg:text-lg font-extrabold text-red-600">🧺 Empty The Ball</h3>
          <button onClick={onCancel} className="text-[#8B7FA8] text-lg w-8 h-8" aria-label="Cancel">✕</button>
        </div>

        <p className="text-xs text-gray-100">
          Your child reached the goal! What did you give them? This resets the machine so they can start filling it up again.
        </p>

        <div className="flex flex-col space-y-1">
          <label className="text-xs font-bold text-white">What reward did you give? *</label>
          <input type="text" value={rewardGiven} onChange={(e) => setRewardGiven(e.target.value)}
            placeholder="e.g. Toffee, trip to the mall"
            className="w-full text-black border border-[#E5DEF5] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]" />
        </div>

        <div className="flex flex-col space-y-1">
          <label className="text-xs font-bold text-white">Remarks (optional)</label>
          <textarea value={remarks} style={{height: "50px"}} onChange={(e) => setRemarks(e.target.value)}
            placeholder="e.g. So proud, finished 7 days straight!" rows={2}
            className="w-full border border-[#E5DEF5] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED] resize-none" />
        </div>

        <button onClick={() => setShowNext((s) => !s)} className="text-xs px-2 py-2 font-semibold text-yellow-200 hover:text-white">
          {showNext ? "− Hide next reward setup" : "+ Set the next reward now (optional)"}
        </button>

        {showNext && (
          <div className="space-y-3 bg-blue-700 rounded-2xl p-3">
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-bold text-white">Next reward</label>
              <input type="text" value={nextRewardDescription} onChange={(e) => setNextRewardDescription(e.target.value)}
                placeholder="e.g. Ice cream trip"
                className="w-full border border-[#E5DEF5] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]" />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-bold text-white">Condition / remarks</label>
              <input type="text" value={nextRewardNote} onChange={(e) => setNextRewardNote(e.target.value)}
                placeholder="e.g. Only if all 5 levels every day"
                className="w-full border border-[#E5DEF5] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]" />
            </div>
          </div>
        )}

        <button
          disabled={!canSubmit || saving}
          onClick={() => onSubmit({ rewardGiven: rewardGiven.trim(), remarks: remarks.trim(), nextRewardDescription: nextRewardDescription.trim() || undefined, nextRewardNote: nextRewardNote.trim() || undefined })}
          className="w-full bg-[#7C3AED] disabled:opacity-40 text-white font-bold rounded-2xl py-3 text-sm active:scale-95 transition-transform"
        >
          {saving ? "Saving…" : "Confirm & Empty The Ball"}
        </button>
      </div>
    </div>
  );
}

function SetRewardModal({ currentConfig, onCancel, onSubmit, saving }) {
  const [streakGoal, setStreakGoal] = useState(currentConfig?.streakGoal || DEFAULT_STREAK_GOAL);
  const [pointsGoal, setPointsGoal] = useState(currentConfig?.pointsGoal || DEFAULT_POINTS_GOAL);
  const [rewardDescription, setRewardDescription] = useState(currentConfig?.rewardDescription || "");
  const [rewardNote, setRewardNote] = useState(currentConfig?.rewardNote || "");

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end" onClick={onCancel}>
      <div className="gb-sheet w-full bg-blue-800 text-white rounded-t-3xl p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm lh:text-lg font-extrabold text-cyan-200">⚙️ Set Reward Goal</h3>
          <button onClick={onCancel} className="text-[#8B7FA8] text-lg w-8 h-8" aria-label="Cancel">✕</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-bold text-white">Streak goal (days)</label>
            <input type="number" min={1} value={streakGoal} onChange={(e) => setStreakGoal(Number(e.target.value))}
              className="w-full text-black border border-[#E5DEF5] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]" />
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-bold text-white">Points goal</label>
            <input type="number" min={100} step={100} value={pointsGoal} onChange={(e) => setPointsGoal(Number(e.target.value))}
              className="w-full border text-black border-[#E5DEF5] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]" />
          </div>
        </div>
        <p className="text-[11px] text-gray-200">Whichever goal is reached first triggers the win.</p>

        <div className="flex flex-col space-y-1">
          <label className="text-xs font-bold text-white">Reward</label>
          <input type="text" value={rewardDescription} onChange={(e) => setRewardDescription(e.target.value)}
            placeholder="e.g. Ice cream trip"
            className="w-full border text-black border-[#E5DEF5] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]" />
        </div>

        <div className="flex flex-col space-y-1">
          <label className="text-xs font-bold text-white">Condition / remarks (optional)</label>
          <textarea value={rewardNote} style={{height: "50px"}} onChange={(e) => setRewardNote(e.target.value)} rows={2}
            placeholder="e.g. Only if all 5 levels every day"
            className="w-full border border-[#E5DEF5] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED] resize-none" />
        </div>

        <button
          disabled={saving}
          onClick={() => onSubmit({ streakGoal, pointsGoal, rewardDescription: rewardDescription.trim(), rewardNote: rewardNote.trim() })}
          className="w-full bg-[#7C3AED] disabled:opacity-40 text-white font-bold rounded-2xl py-3 text-sm active:scale-95 transition-transform"
        >
          {saving ? "Saving…" : "Save Reward Goal"}
        </button>
      </div>
    </div>
  );
}